/**
 * R&D · Decision Engine (Wave D).
 * Every decision answers WHO? WHY? BASED ON WHAT?
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, TextInput } from "../../components/lib";
import { TabBar, ListRow, Drawer, Field, Segmented } from "../../components/lib.overlay";
import { DocumentActions } from "../../components/DocumentActions";
import { AuditTrail } from "../../components/AuditTrail";
import { RND_DECISIONS, RndDecision, RND_PRODUCTS, BLIND_PANELS, FORMULAS, LAB_SESSIONS } from "../../mocks/rndData";

const DEC_META: Record<RndDecision["decision"], { bg: string; fg: string }> = {
  go:          { bg: "var(--ph-success-tint)", fg: "var(--ph-success)"  },
  reformulate: { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)"  },
  hold:        { bg: "var(--ph-ai-tint)",       fg: "var(--ph-ai)"       },
  kill:        { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)"   },
};
function pname(id: string) { return RND_PRODUCTS.find((p) => p.id === id)?.name ?? id; }

type DecTab =
  | "overview" | "why" | "who" | "evidence"
  | "panels" | "formula" | "lab" | "qc"
  | "financial" | "risk" | "alternatives"
  | "chain" | "votes" | "attachments" | "audit";

export default function Decisions() {
  const [tab, setTab] = useState<RndDecision["decision"] | "all">("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<RndDecision | null>(null);
  const [wsTab, setWsTab] = useState<DecTab>("overview");

  const filtered = useMemo(() => RND_DECISIONS.filter((d) =>
    (tab === "all" || d.decision === tab) &&
    (!q || pname(d.productId).toLowerCase().includes(q.toLowerCase()) || d.rationale.toLowerCase().includes(q.toLowerCase())),
  ), [tab, q]);
  const totalBudget = RND_DECISIONS.reduce((s, d) => s + d.budgetImpactEGP, 0);

  const drill = useMemo(() => {
    if (!selected) return null;
    const product = RND_PRODUCTS.find((p) => p.id === selected.productId);
    const panels = BLIND_PANELS.filter((p) => p.productId === selected.productId);
    const formulas = FORMULAS.filter((f) => f.productId === selected.productId);
    const labSessions = LAB_SESSIONS.filter((l) => l.products.includes(selected.productId));
    const approvedFormula = formulas.find((f) => f.status === "approved") || formulas[0];
    return { product, panels, formulas, labSessions, approvedFormula };
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="rd-decisions">
      <Card testId="rd-dec-summary">
        <SectionHeader eyebrow="Governance · WHO · WHY · BASED ON WHAT" title={`${RND_DECISIONS.length} decision${RND_DECISIONS.length === 1 ? "" : "s"}`} />
        <div className="text-sm" style={{ color: "var(--ph-ink-default)" }}>Cumulative R&amp;D budget touched by decisions: EGP {(totalBudget/1_000_000).toFixed(2)}M</div>
      </Card>

      <Card testId="rd-dec-filters">
        <TextInput value={q} onChange={setQ} placeholder="Search decisions…" testId="rd-dec-search" />
        <div className="mt-4">
          <TabBar<RndDecision["decision"] | "all">
            testId="rd-dec-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",         label: "All" },
              { key: "go",          label: "Go" },
              { key: "reformulate", label: "Reformulate" },
              { key: "hold",        label: "Hold" },
              { key: "kill",        label: "Kill" },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="rd-dec-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Log · click to drill" title={`${filtered.length} entr${filtered.length === 1 ? "y" : "ies"}`} />
        </div>
        <div className="p-2">
          {filtered.map((d) => {
            const m = DEC_META[d.decision];
            return (
              <ListRow key={d.id} testId={`rd-dec-${d.id}`}
                onClick={() => { setSelected(d); setWsTab("overview"); }}
                label={<span className="inline-flex items-center gap-2">{pname(d.productId)}
                  <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: m.bg, color: m.fg, borderRadius: "var(--ph-radius-sm)" }}>{d.decision}</span>
                </span>}
                sublabel={<>{d.decidedBy} · {d.dateRelative} — {d.rationale}</>}
                trailing={<span className="text-sm tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>EGP {(d.budgetImpactEGP/1000).toFixed(0)}K</span>}
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Decision 360" testId="rd-dec-drawer" width={780}>
        {selected && drill && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: DEC_META[selected.decision].fg }}>{selected.decision}</div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{pname(selected.productId)}</div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Decision</div><div className="text-sm font-semibold">{selected.decision}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Decided by</div><div className="text-sm font-semibold">{selected.decidedBy}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Budget impact</div><div className="text-sm font-semibold tabular-nums">EGP {selected.budgetImpactEGP.toLocaleString()}</div></div>
            </div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<DecTab>
                testId="rd-dec-ws-tabs"
                value={wsTab}
                onChange={setWsTab}
                options={[
                  { value: "overview",     label: "Overview"     },
                  { value: "why",          label: "Why"          },
                  { value: "who",          label: "Who"          },
                  { value: "evidence",     label: "Evidence"     },
                  { value: "panels",       label: "Blind Panels" },
                  { value: "formula",      label: "Formula"      },
                  { value: "lab",          label: "Lab"          },
                  { value: "qc",           label: "QC"           },
                  { value: "financial",    label: "Financial"    },
                  { value: "risk",         label: "Risk"         },
                  { value: "alternatives", label: "Alternatives" },
                  { value: "chain",        label: "Chain"        },
                  { value: "votes",        label: "Votes"        },
                  { value: "attachments",  label: "Attachments"  },
                  { value: "audit",        label: "Audit"        },
                ]}
              />
            </div>

            {wsTab === "overview" && (
              <div data-testid="rd-dec-ws-overview">
                <Field label="Product"        inline><span className="text-sm">{pname(selected.productId)}</span></Field>
                <Field label="Decision"       inline><span className="text-sm font-semibold" style={{ color: DEC_META[selected.decision].fg }}>{selected.decision}</span></Field>
                <Field label="Decided by"     inline><span className="text-sm">{selected.decidedBy}</span></Field>
                <Field label="When"           inline><span className="text-sm">{selected.dateRelative}</span></Field>
                <Field label="Budget impact"  inline><span className="text-sm tabular-nums">EGP {selected.budgetImpactEGP.toLocaleString()}</span></Field>
                <Field label="Rationale"><span className="text-sm">{selected.rationale}</span></Field>
              </div>
            )}

            {wsTab === "why" && (
              <div data-testid="rd-dec-ws-why">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Reason</div>
                <div className="text-sm p-3 mb-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)" }}>{selected.rationale}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Business justification</div>
                <div className="text-sm p-3 mb-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)" }}>{drill.product ? `Target retail EGP ${drill.product.targetRetailEGP.toLocaleString()} · phase ${drill.product.phase} · ${drill.product.iterations} iterations` : "Product context unavailable"}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Technical justification</div>
                <div className="text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)" }}>{drill.approvedFormula ? `Formula ${drill.approvedFormula.version} · cost EGP ${drill.approvedFormula.costEGPPerLiter}/L · IFRA ${drill.approvedFormula.ifraCompliant ? "OK" : "FAIL"}` : "Awaiting formula lock"}</div>
              </div>
            )}

            {wsTab === "who" && (
              <div data-testid="rd-dec-ws-who">
                <Field label="Requested by"  inline><span className="text-sm">{drill.product?.leadPerfumer || selected.decidedBy}</span></Field>
                <Field label="Recommended by" inline><span className="text-sm">R&amp;D lead</span></Field>
                <Field label="Reviewed by"   inline><span className="text-sm">Finance + Executive</span></Field>
                <Field label="Approved by"   inline><span className="text-sm font-semibold">{selected.decidedBy}</span></Field>
              </div>
            )}

            {wsTab === "evidence" && (
              <div data-testid="rd-dec-ws-evidence">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Evidence trail</div>
                <Field label="Blind panels"  inline><span className="text-sm tabular-nums">{drill.panels.length}</span></Field>
                <Field label="Formulas"      inline><span className="text-sm tabular-nums">{drill.formulas.length}</span></Field>
                <Field label="Lab sessions"  inline><span className="text-sm tabular-nums">{drill.labSessions.length}</span></Field>
                <Field label="Best overall"  inline><span className="text-sm tabular-nums">{drill.panels.length ? Math.max(...drill.panels.map((p) => p.overall)).toFixed(1) : "—"}/10</span></Field>
              </div>
            )}

            {wsTab === "panels" && (
              <div data-testid="rd-dec-ws-panels">
                {drill.panels.map((p) => (
                  <ListRow key={p.id} testId={`rd-dec-p-${p.id}`}
                    label={<>Panel {p.panel} · {p.version}</>}
                    sublabel={<>{p.responsesIn}/{p.panelSize} · {p.recommendation}</>}
                    trailing={<span className="text-sm tabular-nums">{p.overall.toFixed(1)}</span>}
                  />
                ))}
              </div>
            )}

            {wsTab === "formula" && (
              <div data-testid="rd-dec-ws-formula">
                {drill.formulas.map((f) => (
                  <ListRow key={f.id} testId={`rd-dec-f-${f.id}`}
                    label={<>{f.id} · {f.version}</>}
                    sublabel={<>{f.status} · EGP {f.costEGPPerLiter}/L · IFRA {f.ifraCompliant ? "OK" : "FAIL"}</>}
                  />
                ))}
              </div>
            )}

            {wsTab === "lab" && (
              <div data-testid="rd-dec-ws-lab">
                {drill.labSessions.map((l) => (
                  <ListRow key={l.id} testId={`rd-dec-l-${l.id}`}
                    label={l.code}
                    sublabel={<>{l.perfumer} · {l.outcome} · {l.dateRelative}</>}
                  />
                ))}
              </div>
            )}

            {wsTab === "qc" && (
              <div data-testid="rd-dec-ws-qc">
                <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>QC results attach at Batch level once production is triggered.</div>
              </div>
            )}

            {wsTab === "financial" && (
              <div data-testid="rd-dec-ws-financial">
                <Field label="Budget impact" inline><span className="text-sm tabular-nums font-semibold" style={{ color: selected.budgetImpactEGP > 0 ? "var(--ph-danger)" : "var(--ph-success)" }}>EGP {selected.budgetImpactEGP.toLocaleString()}</span></Field>
                {drill.product && <>
                  <Field label="Cost / L"          inline><span className="text-sm tabular-nums">EGP {drill.product.costPerLiterEGP.toLocaleString()}</span></Field>
                  <Field label="Target retail"     inline><span className="text-sm tabular-nums">EGP {drill.product.targetRetailEGP.toLocaleString()}</span></Field>
                  <Field label="Est. gross margin" inline><span className="text-sm tabular-nums">{drill.product.targetRetailEGP > 0 ? Math.round((1 - drill.product.costPerLiterEGP / drill.product.targetRetailEGP) * 100) : 0}%</span></Field>
                </>}
              </div>
            )}

            {wsTab === "risk" && (
              <div data-testid="rd-dec-ws-risk">
                <Field label="IFRA compliance" inline><span className="text-sm" style={{ color: drill.approvedFormula?.ifraCompliant ? "var(--ph-success)" : "var(--ph-danger)" }}>{drill.approvedFormula?.ifraCompliant ? "Compliant" : "Non-compliant"}</span></Field>
                <Field label="Best panel score" inline><span className="text-sm tabular-nums">{drill.panels.length ? Math.max(...drill.panels.map((p) => p.overall)).toFixed(1) : "—"}</span></Field>
                <div className="text-sm p-3 mt-2" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)" }}>
                  {selected.decision === "kill" ? "Kill decision — reputational risk mitigated by not shipping." : selected.decision === "hold" ? "Hold decision — cash preserved; opportunity risk elevated." : "Go decision — execution risk transfers to Operations + Launch."}
                </div>
              </div>
            )}

            {wsTab === "alternatives" && (
              <div data-testid="rd-dec-ws-alt">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Alternative formulas explored</div>
                {drill.formulas.filter((f) => !f.status.includes("approved")).slice(0, 5).map((f) => (
                  <ListRow key={f.id} testId={`rd-dec-alt-${f.id}`}
                    label={<>{f.id} · {f.version}</>}
                    sublabel={<>{f.status} · superseded</>}
                    trailing={<span className="text-sm tabular-nums">EGP {f.costEGPPerLiter}/L</span>}
                  />
                ))}
              </div>
            )}

            {wsTab === "chain" && (
              <div data-testid="rd-dec-ws-chain">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Approval chain</div>
                <ListRow testId="rd-dec-chain-req" label={<>Requester · {drill.product?.leadPerfumer || "R&D"}</>} sublabel="Initiated" />
                <ListRow testId="rd-dec-chain-mgr" label="Department Manager" sublabel="Reviewed" />
                <ListRow testId="rd-dec-chain-fin" label="Finance" sublabel="Budget-checked" />
                <ListRow testId="rd-dec-chain-exec" label={<>Executive · {selected.decidedBy}</>} sublabel={selected.decision.toUpperCase()} />
                <ListRow testId="rd-dec-chain-owner" label="Owner" sublabel="Ratified" />
              </div>
            )}

            {wsTab === "votes" && (
              <div data-testid="rd-dec-ws-votes">
                <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>This decision was resolved by chain-of-command; no vote was required.</div>
              </div>
            )}

            {wsTab === "attachments" && (
              <div data-testid="rd-dec-ws-attachments">
                <DocumentActions docKey="blind-panel-report" recordId={selected.productId} testId={`rd-dec-att-${selected.id}`} compact />
              </div>
            )}

            {wsTab === "audit" && (
              <div data-testid="rd-dec-ws-audit">
                <AuditTrail collection="rnd_decisions" recordId={selected.id} testId="rd-dec-audit" />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
